/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import '../../../style/components/ModuleTable.scss';
import './PerformanceTable.scss';

import isNil from 'lodash/isNil';
import map from 'lodash/map';
import keyBy from 'lodash/keyBy';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import numeral from 'numeral';

import { mmmddyyyy } from 'services/Dates';
import { getAccountPerformance } from 'services/Rest';

import PerformanceWarningMessage from './PerformanceWarningMessage';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { getPerformanceTableData } from 'state/performance/PerformanceSelectors';
import { getSelectedProductClasses } from 'services/Products';
import { ALL_LABEL } from 'services/Filters';

const AccountNumber = ({ accountNumber }) => (
  <Fragment>{accountNumber}</Fragment>
);

const AccountNumberWithPermissions = WithUserPermissions(AccountNumber);

/**
 * Deprecated: Replaced by RateOfReturnBreakdownGrid
 */
class PerformanceTable extends React.Component {
  constructor(props) {
    super(props);

    this.viewAllRowsClick = this.viewAllRowsClick.bind(this);
    this.rowClickHandler = this.rowClickHandler.bind(this);
    this.viewPurgedClick = this.viewPurgedClick.bind(this);
    this.getSelectedAccountPerformance =
      this.getSelectedAccountPerformance.bind(this);

    this.state = {
      viewAllRows: false,
      viewPurgedTable: false
    };
  }

  componentDidMount() {
    this.getSelectedAccountPerformance();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const nextAccounts = nextProps.accounts;
    const brokerageOnly = nextProps.brokerageOnly;

    if (nextAccounts == null) {
      return;
    }

    const currentAccounts = this.props.accounts || [];

    const currentSort = map(currentAccounts, 'accountId').sort().join(',');
    const nextSort = map(nextAccounts, 'accountId').sort().join(',');
    if (!brokerageOnly && currentSort !== nextSort) {
      this.getSelectedAccountPerformance(nextAccounts);
    }
  }

  getSelectedAccountPerformance(nextAccounts) {
    const { accounts } = this.props;
    // use next accounts if we are updating
    getAccountPerformance(
      keyBy(nextAccounts ? nextAccounts : accounts, 'accountId')
    );
  }

  rowClickHandler(event) {
    let el = event.target;

    while (el != null && el.nodeName.toLowerCase() !== 'tr') {
      el = el.parentNode;
    }

    if (el == null) {
      return;
    }

    if (el.classList.contains('cannot-expand')) {
      return;
    }

    el.classList.toggle('expanded');
  }

  viewAllRowsClick() {
    this.setState({ viewAllRows: true });
  }

  viewPurgedClick() {
    this.setState((prevState) => ({
      viewPurgedTable: !prevState.viewPurgedTable
    }));
  }

  rowClassName(row, index, initialVisibleIndex) {
    return (
      row.rowType +
      (this.isValueRow(row) ? '' : ' cannot-expand') +
      (this.state.viewAllRows || index <= initialVisibleIndex
        ? ''
        : ' later-row')
    );
  }

  percent(value) {
    return isNil(value) ? '-' : numeral(value / 100).format('0.00%');
  }

  date(value) {
    return isNil(value) ? '' : mmmddyyyy(value);
  }

  isValueRow(row) {
    return ['fund', 'totals'].includes(row.rowType);
  }

  render() {
    const {
      accountPerformanceTable,
      maxInitialRowsToShow,
      performancePeriods,
      showTotals,
      performanceFilter
    } = this.props;
    const { viewAllRows, viewPurgedTable } = this.state;
    const selectedValueFilter = performanceFilter.accountTypeFilter.selected;

    let performanceTable = accountPerformanceTable || [];

    if (!showTotals) {
      performanceTable = performanceTable.filter(
        (row) => row.rowType !== 'totals'
      );
    }
    const purgedTableConstraints = (row) => {
      if (
        row.rowType === 'purpose' ||
        (row.purgedAccount && row.rowType === 'fund')
      ) {
        return true;
      }
    };

    let purgedPerformanceTable = performanceTable.filter((row) =>
      purgedTableConstraints(row)
    );

    const purgedRowCount = purgedPerformanceTable.filter(
      (row) => row.purgedAccount
    ).length;
    const showPurgedAccountsTable = purgedRowCount > 0;

    performanceTable = performanceTable.filter((row) => !row.purgedAccount);

    const moreClosedAccountRowsAvailableCount =
      purgedPerformanceTable.length - maxInitialRowsToShow + 1;

    const initialVisibleIndex =
      performanceTable.length > maxInitialRowsToShow
        ? maxInitialRowsToShow - 2
        : performanceTable.length;

    const moreRowsAvailableCount =
      performanceTable.length - maxInitialRowsToShow + 1;

    // filtering performanceTable according to selectedValueFilter
    if (selectedValueFilter !== ALL_LABEL) {
      const selectedProductClasses =
        getSelectedProductClasses(selectedValueFilter);
      performanceTable = performanceTable.filter((row) =>
        selectedProductClasses.includes(row.productClass)
      );
      purgedPerformanceTable = purgedPerformanceTable.filter((row) =>
        selectedProductClasses.includes(row.productClass)
      );
    }

    return (
      <Fragment>
        <div
          id="performance-table-container"
          className="module module-container">
          <table className="performance-table">
            <thead>
              <tr className="columns">
                <th className="name" />
                <th className="account">Account</th>
                {performancePeriods.map((period) => (
                  <th key={period.key} className={period.key}>
                    {period.label}
                  </th>
                ))}
                <th className="inceptionDate">Inception Date</th>
              </tr>
            </thead>
            <tbody>
              {performanceTable.map((row, index) => [
                <tr
                  className={this.rowClassName(row, index, initialVisibleIndex)}
                  key={row.rowType + index}
                  onClick={this.rowClickHandler}>
                  <td className="name">
                    {row.label}
                    {row.rowType === 'fund' && row.purgedAccount ? (
                      <span className="purged-indicator">Closed</span>
                    ) : null}
                  </td>
                  <td className="account">
                    <AccountNumberWithPermissions
                      accountNumber={row.number}
                      shouldMask
                    />
                  </td>
                  {performancePeriods.map((period) => (
                    <td key={period.key} className={period.key}>
                      {this.isValueRow(row)
                        ? this.percent(row[period.key])
                        : ''}
                    </td>
                  ))}
                  <td className="inceptionDate">
                    {this.date(row.inceptionDate)}
                  </td>
                </tr>,
                viewAllRows || index !== initialVisibleIndex ? null : (
                  <tr className="view-more-link-row" key="view-more-link">
                    <td
                      className="view-more-link-cell"
                      colSpan={performancePeriods.length + 3}>
                      <a
                        className="view-more-link"
                        onClick={this.viewAllRowsClick}>
                        View complete Personal Rate of Return breakdown.{' '}
                        {moreRowsAvailableCount} more rows available.
                      </a>
                    </td>
                  </tr>
                )
              ])}
            </tbody>
          </table>
          <PerformanceWarningMessage stateProps="accountPerformance" />
        </div>
        {showPurgedAccountsTable &&
          (viewPurgedTable === false ? (
            <div
              id="purged-performance-table-container"
              className="module module-container">
              <table className="performance-table">
                <thead>
                  <tr className="view-more-link-row" key="view-more-link">
                    <td
                      className="view-more-link-cell"
                      colSpan={performancePeriods.length + 3}>
                      <a
                        className="purged-accounts-link"
                        onClick={this.viewPurgedClick}>
                        Show {purgedRowCount} Closed Account(s)
                        <span className={'chevron aa-chevron-down'} />
                      </a>
                    </td>
                  </tr>
                </thead>
              </table>
            </div>
          ) : (
            <div
              id="purged-performance-table-container"
              className="module module-container">
              <table className="performance-table">
                <thead>
                  <tr className="view-more-link-row" key="view-more-link">
                    <td
                      className="view-more-link-cell"
                      colSpan={performancePeriods.length + 3}>
                      <a
                        className="purged-accounts-link"
                        onClick={this.viewPurgedClick}>
                        Hide {purgedRowCount} Closed Account(s)
                        <span className={'chevron aa-chevron-up'} />
                      </a>
                    </td>
                  </tr>
                  <tr className="columns">
                    <th className="name" />
                    <th className="account">Account</th>
                    {performancePeriods.map((period) => (
                      <th key={period.key} className={period.key} />
                    ))}
                    <th className="inceptionDate">Inception Date</th>
                  </tr>
                </thead>
                <tbody>
                  {purgedPerformanceTable.map((row, index) => [
                    <tr
                      className={this.rowClassName(
                        row,
                        index,
                        initialVisibleIndex
                      )}
                      key={row.rowType + index}
                      onClick={this.rowClickHandler}>
                      <td className="name">
                        {row.label}
                        {row.rowType === 'fund' && row.purgedAccount ? (
                          <span className="purged-indicator">Closed</span>
                        ) : null}
                      </td>
                      <td className="account">{row.number}</td>
                      {performancePeriods.map((period) => (
                        <td key={period.key} className={period.key}>
                          {this.isValueRow(row)
                            ? this.percent(row[period.key])
                            : ''}
                        </td>
                      ))}
                      <td className="inceptionDate">
                        {this.date(row.inceptionDate)}
                      </td>
                    </tr>,
                    viewAllRows || index !== initialVisibleIndex ? null : (
                      <tr className="view-more-link-row" key="view-more-link">
                        <td
                          className="view-more-link-cell"
                          colSpan={performancePeriods.length + 3}>
                          <a
                            className="view-more-link"
                            onClick={this.viewAllRowsClick}>
                            View complete Personal Rate of Return breakdown.{' '}
                            {moreClosedAccountRowsAvailableCount} more rows
                            available.
                          </a>
                        </td>
                      </tr>
                    )
                  ])}
                </tbody>
              </table>
              <PerformanceWarningMessage stateProps="accountPerformance" />
            </div>
          ))}
      </Fragment>
    );
  }
}

PerformanceTable.propTypes = {};

const mapStateToProps = (state) => {
  const { performancePeriods, selections } = state;
  const { accounts, brokerageOnly } = selections;
  const { maxInitialRowsToShow, hasActivePlus, hasActiveAdvisory } = state.view;

  const showTotals = !hasActivePlus && !hasActiveAdvisory;

  return {
    accounts,
    brokerageOnly,
    showTotals,
    accountPerformanceTable: getPerformanceTableData(state),
    performancePeriods,
    maxInitialRowsToShow,
    performanceFilter: state.performanceFilter
  };
};

export default connect(mapStateToProps)(PerformanceTable);
